/**
 * Tiny BM25 retrieval over knowledge-base passages.
 * Inverted index: token → Map(passageId → termFrequency). Lookups are hash-map
 * reads, so a query costs O(queryTokens × matchingPassages), not O(corpus).
 */
const STOP = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'do', 'does', 'did', 'can', 'could', 'you', 'your', 'he', 'his', 'him', 'me', 'i', 'to', 'of', 'in', 'on', 'for', 'and', 'or', 'about', 'tell', 'what', 'which', 'who', 'how', 'please', 'with', 'at', 'it', 'this', 'that', 'any', 'some', 'have', 'has', 'be', 'rakshan', 'shetty', 'u', 'ur', 'there', 'their', 'from', 'by', 'as', 'into', 'than', 'then', 'so']);

/** Light stemmer: plurals, -ing, -ed, -tion → base. Good enough for matching, cheap. */
export const stem = (w) => {
  if (w.length <= 3) return w;
  return w
    .replace(/(ies)$/, 'y')
    .replace(/(sses)$/, 'ss')
    .replace(/([^s])s$/, '$1')
    .replace(/(ing|ed|ly)$/, '')
    .replace(/(ation|tion)$/, 't')
    .replace(/(er|or)$/, '');
};

/** Query-side synonyms → canonical knowledge-base vocabulary. */
export const SYNONYMS = {
  job: 'role', jobs: 'role', employer: 'company', firm: 'company', org: 'company', workplace: 'company',
  gig: 'freelance', hire: 'available', hiring: 'available', recruit: 'available', vacancy: 'available',
  uni: 'college', university: 'college', school: 'education', studied: 'education', qualification: 'education',
  marks: 'score', grade: 'score', grades: 'score', percentage: 'score',
  mail: 'email', gmail: 'email', number: 'phone', mobile: 'phone', call: 'phone', whatsapp: 'phone',
  cv: 'resume', biodata: 'resume',
  app: 'application', apps: 'application', website: 'web', site: 'web', webapp: 'web',
  shop: 'ecommerce', store: 'ecommerce', 'e-commerce': 'ecommerce', marketplace: 'ecommerce', storefront: 'ecommerce',
  frontend: 'front-end', backend: 'back-end', db: 'database', sql: 'database', postgres: 'postgresql', mongo: 'mongodb',
  ai: 'ml', 'ai/ml': 'ml', genai: 'llm', gpt: 'llm', chatbot: 'rag', bot: 'automation',
  k8s: 'kubernetes', ci: 'cicd', cd: 'cicd', 'ci/cd': 'cicd', pipeline: 'cicd', deploy: 'deployment',
  pay: 'salary', ctc: 'salary', package: 'salary', compensation: 'salary', rate: 'salary', charges: 'salary', cost: 'salary',
  lives: 'location', based: 'location', city: 'location', where: 'location', wfh: 'remote', onsite: 'hybrid',
  award: 'achievement', awards: 'achievement', won: 'achievement', wins: 'achievement',
  accolades: 'achievement', accomplishments: 'achievement',
  certificate: 'certification', certificates: 'certification', certified: 'certification', course: 'certification',
  internship: 'intern', trainee: 'intern', fresher: 'intern',
  typescript: 'typescript', ts: 'typescript', js: 'javascript', reactjs: 'react', nextjs: 'next', nestjs: 'nest', nodejs: 'node', expressjs: 'express',
};

export const tokenize = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9+#./-]+/g, ' ')
    .split(' ')
    .map((t) => t.replace(/^[./-]+|[./-]+$/g, ''))
    .filter((t) => t && !STOP.has(t))
    .map((t) => SYNONYMS[t] || t)
    .map((t) => stem(t.replace(/\.js$/, '')));

export function buildIndex(passages) {
  const inverted = new Map();
  const docLen = new Array(passages.length);
  let total = 0;
  passages.forEach((p, id) => {
    const toks = tokenize(`${p.title} ${p.title} ${p.text} ${(p.tags || []).join(' ')}`);
    docLen[id] = toks.length;
    total += toks.length;
    for (const t of toks) {
      let posting = inverted.get(t);
      if (!posting) { posting = new Map(); inverted.set(t, posting); }
      posting.set(id, (posting.get(id) || 0) + 1);
    }
  });
  const avgLen = total / Math.max(1, passages.length);
  const N = passages.length;
  const k1 = 1.4, b = 0.75;

  const search = (query, limit = 3) => {
    const qToks = [...new Set(tokenize(query))];
    const scores = new Map();
    for (const t of qToks) {
      let posting = inverted.get(t);
      if (!posting) {
        // typo tolerance: nearest indexed token within edit distance 1 (len ≥ 5)
        if (t.length >= 5) for (const key of inverted.keys()) if (Math.abs(key.length - t.length) <= 1 && editDistance(key, t) <= 1) { posting = inverted.get(key); break; }
        if (!posting) continue;
      }
      const idf = Math.log(1 + (N - posting.size + 0.5) / (posting.size + 0.5));
      for (const [id, tf] of posting) {
        const norm = tf * (k1 + 1) / (tf + k1 * (1 - b + b * docLen[id] / avgLen));
        scores.set(id, (scores.get(id) || 0) + idf * norm);
      }
    }
    return [...scores.entries()]
      .sort((a, b2) => b2[1] - a[1])
      .slice(0, limit)
      .map(([id, score]) => ({ ...passages[id], id, score, matched: qToks.length }));
  };

  return { search, size: passages.length, vocabulary: inverted.size };
}

function editDistance(a, b) {
  const m = a.length, n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[n];
}
